<template>
    <div class="mall-v2-page">
        <a-spin :spinning="loading">
            <div class="mall-v2-header">
                <div>
                    <div class="mall-v2-title">商城装修 V2</div>
                    <div class="mall-v2-subtitle">整套管理首页、开卡页、个人中心和购物车草稿，并统一发布</div>
                </div>
                <div class="mall-v2-actions">
                    <a-switch :checked="useDecorateV2" checked-children="V2" un-checked-children="V1" @change="toggleV2" />
                    <a-button icon="reload" @click="loadAll">刷新</a-button>
                    <a-button icon="copy" @click="openCopyDecorateModal">复制装修</a-button>
                    <a-button type="primary" icon="cloud-upload" @click="publishAll">整套发布</a-button>
                </div>
            </div>

            <div class="mall-v2-layout">
                <div class="mall-v2-sidebar">
                    <a-tabs default-active-key="page" size="small">
                        <a-tab-pane key="page" tab="页面">
                            <a-button class="custom-page-add" size="small" type="primary" icon="plus" @click="openCustomPageModal('add')">新增自定义页</a-button>
                            <a-empty v-if="!pageList.length" description="暂无页面配置，请确认店铺ID后刷新" />
                            <a-list :data-source="pageList" size="small">
                                <a-list-item slot="renderItem" slot-scope="item" :class="{active: currentPage && currentPage.pageType === item.pageType}" @click="selectPage(item.pageType)">
                                    <div class="page-list-item-main">
                                        <div class="mall-v2-list-title">{{ getPageLabel(item) }}</div>
                                        <div class="mall-v2-list-desc">{{ getPageDesc(item) }}</div>
                                    </div>
                                    <div v-if="isCustomPage(item.pageType)" class="page-list-actions" @click.stop>
                                        <a-button size="small" icon="edit" @click="openCustomPageModal('rename', item)" />
                                        <a-popconfirm title="删除后已配置的跳转链接会失效，确认删除？" @confirm="deleteCustomPage(item)">
                                            <a-button size="small" icon="delete" type="danger" />
                                        </a-popconfirm>
                                    </div>
                                </a-list-item>
                            </a-list>
                        </a-tab-pane>
                        <a-tab-pane key="template" tab="模板">
                            <div v-if="themeList.length" class="theme-section">
                                <div class="sidebar-section-title">整店风格</div>
                                <div class="sidebar-section-desc">统一控制所有页面的主题和主色</div>
                                <div
                                    v-for="theme in themeList"
                                    :key="getThemeCode(theme)"
                                    class="theme-card"
                                    :class="{active: activeThemeCode === getThemeCode(theme)}"
                                    @click="applyTheme(theme)"
                                >
                                    <span class="theme-swatch" :style="{ backgroundColor: getThemePrimaryColor(theme) }"></span>
                                    <div class="theme-card-main">
                                        <div class="mall-v2-list-title">{{ getThemeName(theme) }}</div>
                                        <div class="mall-v2-list-desc">{{ getThemePrimaryColor(theme) }}</div>
                                    </div>
                                    <a-icon v-if="activeThemeCode === getThemeCode(theme)" type="check" />
                                </div>
                            </div>
                            <div v-if="currentPageType === 'HOME'" class="theme-section home-title-config">
                                <div class="sidebar-section-title">首页标题栏</div>
                                <div class="sidebar-section-desc">控制首页顶部标题栏和搜索栏上方留白</div>
                                <div class="sidebar-config-row">
                                    <span>显示标题栏</span>
                                    <a-switch :checked="getPageConfig('showTitleBar', true)" @change="value => updatePageConfig('showTitleBar', value)" />
                                </div>
                                <div class="sidebar-config-field">
                                    <label>标题来源</label>
                                    <a-radio-group class="home-title-mode-group" :value="homeTitleMode" @change="e => updateHomeTitleMode(e.target.value)">
                                        <a-radio-button value="shopName">商城名称</a-radio-button>
                                        <a-radio-button value="custom">自定义</a-radio-button>
                                    </a-radio-group>
                                </div>
                                <div v-if="homeTitleMode === 'shopName'" class="sidebar-config-field">
                                    <label>当前名称</label>
                                    <a-input :value="homeStoreShopName" disabled />
                                </div>
                                <div class="sidebar-config-field">
                                    <label>标题文字</label>
                                    <a-input :value="homeTitleText" :disabled="homeTitleMode === 'shopName'" @change="e => updatePageConfig('pageTitle', e.target.value)" />
                                </div>
                                <div class="sidebar-config-field">
                                    <label>背景色</label>
                                    <a-input :value="getPageConfig('titleBarBackgroundColor', '#ffffff')" @change="e => updatePageConfig('titleBarBackgroundColor', e.target.value)" />
                                </div>
                                <div class="sidebar-config-field">
                                    <label>文字色</label>
                                    <a-input :value="getPageConfig('titleBarTextColor', '#222222')" @change="e => updatePageConfig('titleBarTextColor', e.target.value)" />
                                </div>
                            </div>
                            <a-list :data-source="templateList" size="small">
                                <a-list-item slot="renderItem" slot-scope="item">
                                    <div class="template-row">
                                        <div>
                                            <div class="mall-v2-list-title">{{ item.templateName }}</div>
                                            <div class="mall-v2-list-desc">{{ item.templateCode }}</div>
                                        </div>
                                        <a-button size="small" @click="applyTemplate(item)">应用</a-button>
                                    </div>
                                </a-list-item>
                            </a-list>
                        </a-tab-pane>
                        <a-tab-pane key="control" tab="控件库">
                            <a-empty v-if="!isComponentPage" description="当前页面使用专用配置，不支持插入首页控件" />
                            <div v-else class="sidebar-control-list">
                                <div v-for="preset in activeControlPresets" :key="preset.type" class="control-card sidebar-control-card">
                                    <div class="control-card-title">{{ preset.label }}</div>
                                    <div class="control-card-desc">{{ preset.description }}</div>
                                    <a-button size="small" type="primary" @click="insertPreset(preset)">插入</a-button>
                                </div>
                            </div>
                        </a-tab-pane>
                    </a-tabs>
                </div>

                <div class="mall-v2-preview">
                    <div class="phone-frame">
                        <div class="phone-title">{{ currentPage ? getPageLabel(currentPage) : '页面预览' }}</div>
                        <mall-v2-preview
                            :content="parsedContent"
                            :selected-index="selectedComponentIndex"
                            :editable="isComponentPage"
                            @select="selectComponent"
                            @move="moveComponent"
                            @duplicate="duplicateComponent"
                            @remove="removeComponent"
                        />
                    </div>
                </div>

                <div class="mall-v2-editor">
                    <div class="editor-title-row">
                        <div>
                            <div class="editor-title">详细设置</div>
                            <div class="editor-subtitle">点击手机预览中的模块后，在这里修改具体内容</div>
                        </div>
                        <a-tag v-if="isComponentPage && draftComponents.length" color="blue">{{ draftComponents.length }} 个控件</a-tag>
                        <a-tag v-if="!isComponentPage" color="green">专用配置</a-tag>
                    </div>

                    <a-empty v-if="isComponentPage && !selectedComponent" description="请先在手机预览中选择一个控件，或从左侧控件库插入控件" />

                    <div v-if="!isComponentPage" class="config-panel page-config-panel">
                        <template v-if="currentPage && currentPage.pageType === 'OPEN_CARD'">
                            <div class="config-section-title">页面内容</div>
                            <div class="config-row">
                                <label>页面标题</label>
                                <a-input :value="getPageConfig('pageTitle', '会员开卡')" @change="e => updatePageConfig('pageTitle', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>主标题</label>
                                <a-input :value="getPageConfig('heroTitle', '开通会员，享专属权益')" @change="e => updatePageConfig('heroTitle', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>副标题</label>
                                <a-textarea :value="getPageConfig('heroSubtitle', '会员价、积分、生日礼，更多福利持续解锁')" :auto-size="{ minRows: 2, maxRows: 4 }" @change="e => updatePageConfig('heroSubtitle', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>会员说明</label>
                                <a-textarea :value="getPageConfig('summaryText')" :auto-size="{ minRows: 2, maxRows: 4 }" @change="e => updatePageConfig('summaryText', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>按钮文案</label>
                                <a-input :value="getPageConfig('buttonText', '立即开卡')" @change="e => updatePageConfig('buttonText', e.target.value)" />
                            </div>
                            <div class="config-section-title">视觉素材</div>
                            <div class="config-row">
                                <label>显示主图</label>
                                <a-switch :checked="getPageConfig('showBanner', true)" @change="value => updatePageConfig('showBanner', value)" />
                            </div>
                            <div class="config-row">
                                <label>开卡主图</label>
                                <a-input-group compact>
                                    <a-input :value="getPageConfig('bannerUrl')" style="width: calc(100% - 88px)" @change="e => updatePageConfig('bannerUrl', e.target.value)" />
                                    <a-button icon="picture" @click="openPageImagePicker('bannerUrl')">选择</a-button>
                                </a-input-group>
                            </div>
                            <div class="config-row">
                                <label>展示方式</label>
                                <a-radio-group :value="getPageConfig('bannerMode', 'card')" @change="e => updatePageConfig('bannerMode', e.target.value)">
                                    <a-radio-button value="card">卡片图</a-radio-button>
                                    <a-radio-button value="wide">大图</a-radio-button>
                                    <a-radio-button value="none">不显示</a-radio-button>
                                </a-radio-group>
                            </div>
                            <div class="config-section-title">会员权益</div>
                            <div class="config-row config-row-top">
                                <label>权益列表</label>
                                <a-textarea :value="openCardFeatureText" :auto-size="{ minRows: 4, maxRows: 8 }" placeholder="每行一条，格式：标题|说明|图标编码" @change="e => updateOpenCardFeatures(e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>隐私提示</label>
                                <a-textarea :value="getPageConfig('privacyText', '手机号仅用于会员身份识别和权益发放')" :auto-size="{ minRows: 2, maxRows: 4 }" @change="e => updatePageConfig('privacyText', e.target.value)" />
                            </div>
                            <div class="config-section-title">表单字段</div>
                            <div class="config-row">
                                <label>显示昵称</label>
                                <a-switch :checked="getPageConfig('showName', true)" @change="value => updatePageConfig('showName', value)" />
                            </div>
                            <div class="config-row">
                                <label>昵称必填</label>
                                <a-switch :checked="getPageConfig('mustInputName', false)" @change="value => updatePageConfig('mustInputName', value)" />
                            </div>
                            <div class="config-row">
                                <label>显示生日</label>
                                <a-switch :checked="getPageConfig('showBirthDay', true)" @change="value => updatePageConfig('showBirthDay', value)" />
                            </div>
                            <div class="config-row">
                                <label>生日必填</label>
                                <a-switch :checked="getPageConfig('mustInputBirth', false)" @change="value => updatePageConfig('mustInputBirth', value)" />
                            </div>
                        </template>

                        <template v-else-if="currentPage && currentPage.pageType === 'USER_CENTER'">
                            <div class="config-section-title">页面内容</div>
                            <div class="config-row">
                                <label>页面标题</label>
                                <a-input :value="getPageConfig('pageTitle', '个人中心')" @change="e => updatePageConfig('pageTitle', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>主标题</label>
                                <a-input :value="getPageConfig('heroTitle', '我的会员中心')" @change="e => updatePageConfig('heroTitle', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>副标题</label>
                                <a-textarea :value="getPageConfig('heroSubtitle', '查看订单、积分、余额和专属权益')" :auto-size="{ minRows: 2, maxRows: 4 }" @change="e => updatePageConfig('heroSubtitle', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>说明文案</label>
                                <a-textarea :value="getPageConfig('summaryText')" :auto-size="{ minRows: 2, maxRows: 4 }" @change="e => updatePageConfig('summaryText', e.target.value)" />
                            </div>
                            <div class="config-section-title">会员卡</div>
                            <div class="config-row">
                                <label>显示卡图</label>
                                <a-switch :checked="getPageConfig('showVipCardImage', true)" @change="value => updatePageConfig('showVipCardImage', value)" />
                            </div>
                            <div class="config-row">
                                <label>会员卡图</label>
                                <a-input-group compact>
                                    <a-input :value="getPageConfig('vipCardImage')" style="width: calc(100% - 88px)" @change="e => updatePageConfig('vipCardImage', e.target.value)" />
                                    <a-button icon="picture" @click="openPageImagePicker('vipCardImage')">选择</a-button>
                                </a-input-group>
                            </div>
                            <div class="config-row">
                                <label>卡片样式</label>
                                <a-radio-group :value="getPageConfig('cardMode', 'image')" @change="e => updatePageConfig('cardMode', e.target.value)">
                                    <a-radio-button value="dark">深色卡</a-radio-button>
                                    <a-radio-button value="light">浅色卡</a-radio-button>
                                    <a-radio-button value="image">图片卡</a-radio-button>
                                </a-radio-group>
                            </div>
                            <div class="config-section-title">会员资产</div>
                            <div class="config-row">
                                <label>资产标题</label>
                                <a-input :value="getPageConfig('assetTitle', '我的资产')" @change="e => updatePageConfig('assetTitle', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>显示余额</label>
                                <a-switch :checked="getPageConfig('showMoney', true)" @change="value => updatePageConfig('showMoney', value)" />
                            </div>
                            <div class="config-row">
                                <label>显示积分</label>
                                <a-switch :checked="getPageConfig('showPoint', true)" @change="value => updatePageConfig('showPoint', value)" />
                            </div>
                            <div class="config-row">
                                <label>显示优惠券</label>
                                <a-switch :checked="getPageConfig('showCoupon', true)" @change="value => updatePageConfig('showCoupon', value)" />
                            </div>
                            <div class="config-section-title">我的订单</div>
                            <div class="config-row">
                                <label>订单标题</label>
                                <a-input :value="getPageConfig('orderTitle', '我的订单')" @change="e => updatePageConfig('orderTitle', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>显示订单</label>
                                <a-switch :checked="getPageConfig('showOrder', true)" @change="value => updatePageConfig('showOrder', value)" />
                            </div>
                            <div class="config-row">
                                <label>全部订单</label>
                                <a-switch :checked="getPageConfig('showAllOrder', true)" @change="value => updatePageConfig('showAllOrder', value)" />
                            </div>
                            <div class="config-section-title">常用服务</div>
                            <div class="config-row">
                                <label>菜单标题</label>
                                <a-input :value="getPageConfig('menuTitle', '常用服务')" @change="e => updatePageConfig('menuTitle', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>显示菜单</label>
                                <a-switch :checked="getPageConfig('showMenu', true)" @change="value => updatePageConfig('showMenu', value)" />
                            </div>
                            <div class="config-row config-row-top">
                                <label>服务说明</label>
                                <a-textarea :value="userCenterFeatureText" :auto-size="{ minRows: 4, maxRows: 8 }" placeholder="每行一条，格式：标题|说明|图标编码" @change="e => updateUserCenterFeatures(e.target.value)" />
                            </div>
                        </template>

                        <template v-else-if="currentPage && currentPage.pageType === 'CART'">
                            <div class="config-row">
                                <label>空车文案</label>
                                <a-input :value="getPageConfig('emptyText', '购物车空空如也')" @change="e => updatePageConfig('emptyText', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>按钮文案</label>
                                <a-input :value="getPageConfig('goShoppingText', '去逛逛')" @change="e => updatePageConfig('goShoppingText', e.target.value)" />
                            </div>
                        </template>

                        <template v-else-if="currentPage && currentPage.pageType === 'PRODUCT_DETAIL'">
                            <div class="config-section-title">商品信息</div>
                            <div class="config-row">
                                <label>显示库存</label>
                                <a-switch :checked="getPageConfig('showStock', true)" @change="value => updatePageConfig('showStock', value)" />
                            </div>
                        </template>
                    </div>

                    <div v-if="selectedComponent" class="editor-title-row editor-gap">
                        <div>
                            <div class="editor-title">控件配置</div>
                            <div class="editor-subtitle">配置当前选中的控件，改完会立即刷新预览</div>
                        </div>
                    </div>

                    <div v-if="selectedComponent" class="config-panel">
                        <div class="config-row">
                            <label>显示控件</label>
                            <a-switch :checked="selectedComponent.visible !== false" @change="value => updateSelectedField('visible', value)" />
                        </div>

                        <template v-if="selectedComponent.type === 'searchBar'">
                            <div class="config-row">
                                <label>提示文字</label>
                                <a-input :value="getSelectedProp('placeholder')" @change="e => updateSelectedProp('placeholder', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>点击跳转</label>
                                <a-select :value="getSelectedProp('linkType', 'systemPage')" style="width: 100%" @change="value => updateSearchLinkType(value)">
                                    <a-select-option value="none">不跳转</a-select-option>
                                    <a-select-option value="systemPage">系统页面</a-select-option>
                                    <a-select-option value="customPage">自定义页面</a-select-option>
                                </a-select>
                            </div>
                            <div v-if="getSelectedProp('linkType', 'systemPage') === 'systemPage'" class="config-row">
                                <label>系统页面</label>
                                <a-select :value="getSelectedProp('linkTarget', 'pages/search/search')" style="width: 100%" @change="value => updateSearchLinkTarget('systemPage', value)">
                                    <a-select-option v-for="page in systemPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                </a-select>
                            </div>
                            <div v-if="getSelectedProp('linkType', 'systemPage') === 'customPage'" class="config-row">
                                <label>自定义页面</label>
                                <a-select :value="getSelectedProp('linkTarget')" placeholder="选择自定义页面" style="width: 100%" @change="value => updateSearchLinkTarget('customPage', value)">
                                    <a-select-option v-for="page in customPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                </a-select>
                            </div>
                        </template>

                        <template v-else-if="selectedComponent.type === 'heroSwiper'">
                            <div class="config-row">
                                <label>轮播高度</label>
                                <a-input-number :value="getSelectedProp('height', 360)" :min="220" :max="520" @change="value => updateSelectedProp('height', value)" />
                            </div>
                            <div class="config-row">
                                <label>轮播图片</label>
                                <div class="swiper-editor-list">
                                    <div v-for="(item, index) in swiperItems" :key="index" class="swiper-editor-item">
                                        <div class="swiper-editor-preview">
                                            <img v-if="item.imageUrl" :src="resolveEditorImage(item.imageUrl)" />
                                            <span v-else>暂无图片</span>
                                        </div>
                                        <div class="swiper-editor-main">
                                            <div class="swiper-editor-head">
                                                <span>图片 {{ index + 1 }}</span>
                                                <div>
                                                    <a-button size="small" icon="arrow-up" :disabled="index === 0" @click="moveSwiperItem(index, -1)" />
                                                    <a-button size="small" icon="arrow-down" :disabled="index === swiperItems.length - 1" @click="moveSwiperItem(index, 1)" />
                                                    <a-button size="small" icon="delete" type="danger" @click="removeSwiperItem(index)" />
                                                </div>
                                            </div>
                                            <a-input-group compact>
                                                <a-input :value="item.imageUrl" placeholder="图片路径" style="width: calc(100% - 88px)" @change="e => updateSwiperImage(index, e.target.value)" />
                                                <a-button icon="picture" @click="openComponentImagePicker('swiperItems', { action: 'replace', index })">替换</a-button>
                                            </a-input-group>
                                            <div class="swiper-editor-row">
                                                <a-select :value="item.linkType || 'none'" style="width: 120px" @change="value => updateSwiperLink(index, 'linkType', value)">
                                                    <a-select-option value="none">不跳转</a-select-option>
                                                    <a-select-option value="systemPage">系统页面</a-select-option>
                                                    <a-select-option value="customPage">自定义页面</a-select-option>
                                                </a-select>
                                                <a-select v-if="item.linkType === 'systemPage'" :value="item.linkTarget" placeholder="选择系统页面" style="flex: 1" @change="value => updateSwiperLinkTarget(index, 'systemPage', value)">
                                                    <a-select-option v-for="page in systemPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                                </a-select>
                                                <a-select v-if="item.linkType === 'customPage'" :value="item.linkTarget" placeholder="选择自定义页面" style="flex: 1" @change="value => updateSwiperLinkTarget(index, 'customPage', value)">
                                                    <a-select-option v-for="page in customPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                                </a-select>
                                            </div>
                                        </div>
                                    </div>
                                    <a-empty v-if="!swiperItems.length" description="暂无轮播图片" />
                                    <a-button icon="plus" type="dashed" @click="openComponentImagePicker('swiperItems', { action: 'append' })">新增图片</a-button>
                                </div>
                            </div>
                        </template>

                        <template v-else-if="selectedComponent.type === 'iconNav'">
                            <div class="config-row">
                                <label>导航标题</label>
                                <a-input :value="getSelectedProp('title')" @change="e => updateSelectedProp('title', e.target.value)" />
                            </div>
                            <div class="config-row config-row-top">
                                <label>导航项</label>
                                <div class="nav-editor-list">
                                    <div v-for="(nav, index) in navItems" :key="index" class="nav-editor-item">
                                        <div class="nav-editor-head">
                                            <span>入口 {{ index + 1 }}</span>
                                            <div>
                                                <a-button size="small" icon="arrow-up" :disabled="index === 0" @click="moveNavItem(index, -1)" />
                                                <a-button size="small" icon="arrow-down" :disabled="index === navItems.length - 1" @click="moveNavItem(index, 1)" />
                                                <a-button size="small" icon="delete" type="danger" @click="removeNavItem(index)" />
                                            </div>
                                        </div>
                                        <a-input :value="nav.label || nav.title" placeholder="入口名称" @change="e => updateNavItem(index, 'label', e.target.value)" />
                                        <div class="nav-editor-row">
                                            <a-select :value="nav.iconType || 'preset'" style="width: 120px" @change="value => updateNavItem(index, 'iconType', value)">
                                                <a-select-option value="preset">内置图标</a-select-option>
                                                <a-select-option value="image">图片图标</a-select-option>
                                            </a-select>
                                            <a-select v-if="(nav.iconType || 'preset') === 'preset'" :value="nav.icon || 'new'" style="flex: 1" @change="value => updateNavItem(index, 'icon', value)">
                                                <a-select-option v-for="icon in navIconOptions" :key="icon.value" :value="icon.value">{{ icon.label }}</a-select-option>
                                            </a-select>
                                            <a-input-group v-else compact style="flex: 1">
                                                <a-input :value="nav.iconImage" placeholder="图片路径" style="width: calc(100% - 88px)" @change="e => updateNavItem(index, 'iconImage', e.target.value)" />
                                                <a-button icon="picture" @click="openNavIconImagePicker(index)">选择</a-button>
                                            </a-input-group>
                                        </div>
                                        <div class="nav-editor-row">
                                            <a-select :value="nav.linkType || 'none'" style="width: 120px" @change="value => updateNavItem(index, 'linkType', value)">
                                                <a-select-option value="none">不跳转</a-select-option>
                                                <a-select-option value="systemPage">系统页面</a-select-option>
                                                <a-select-option value="customPage">自定义页面</a-select-option>
                                            </a-select>
                                            <a-select v-if="nav.linkType === 'systemPage'" :value="nav.linkTarget" placeholder="选择系统页面" style="flex: 1" @change="value => updateNavLinkTarget(index, 'systemPage', value)">
                                                <a-select-option v-for="page in systemPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                            </a-select>
                                            <a-select v-if="nav.linkType === 'customPage'" :value="nav.linkTarget" placeholder="选择自定义页面" style="flex: 1" @change="value => updateNavLinkTarget(index, 'customPage', value)">
                                                <a-select-option v-for="page in customPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                            </a-select>
                                        </div>
                                    </div>
                                    <a-button icon="plus" @click="addNavItem">新增导航项</a-button>
                                </div>
                            </div>
                        </template>

                        <template v-else-if="selectedComponent.type === 'sectionTitle'">
                            <div class="config-row">
                                <label>主标题</label>
                                <a-input :value="getSelectedProp('title')" @change="e => updateSelectedProp('title', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>副标题</label>
                                <a-input :value="getSelectedProp('subtitle')" @change="e => updateSelectedProp('subtitle', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>更多文字</label>
                                <a-input :value="getSelectedProp('moreText')" @change="e => updateSelectedProp('moreText', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>更多跳转</label>
                                <a-select :value="getSelectedProp('linkType', 'none')" style="width: 100%" @change="value => updateSelectedLinkType(value)">
                                    <a-select-option value="none">不跳转</a-select-option>
                                    <a-select-option value="systemPage">系统页面</a-select-option>
                                    <a-select-option value="customPage">自定义页面</a-select-option>
                                </a-select>
                            </div>
                            <div v-if="getSelectedProp('linkType', 'none') === 'systemPage'" class="config-row">
                                <label>系统页面</label>
                                <a-select :value="getSelectedProp('linkTarget')" placeholder="选择系统页面" style="width: 100%" @change="value => updateSelectedLinkTarget('systemPage', value)">
                                    <a-select-option v-for="page in systemPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                </a-select>
                            </div>
                            <div v-if="getSelectedProp('linkType', 'none') === 'customPage'" class="config-row">
                                <label>自定义页面</label>
                                <a-select :value="getSelectedProp('linkTarget')" placeholder="选择自定义页面" style="width: 100%" @change="value => updateSelectedLinkTarget('customPage', value)">
                                    <a-select-option v-for="page in customPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                </a-select>
                            </div>
                        </template>

                        <template v-else-if="selectedComponent.type === 'noticeBar'">
                            <div class="config-row">
                                <label>公告内容</label>
                                <a-input :value="getSelectedProp('text')" @change="e => updateSelectedProp('text', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>公告跳转</label>
                                <a-select :value="getSelectedProp('linkType', 'none')" style="width: 100%" @change="value => updateSelectedLinkType(value)">
                                    <a-select-option value="none">不跳转</a-select-option>
                                    <a-select-option value="systemPage">系统页面</a-select-option>
                                    <a-select-option value="customPage">自定义页面</a-select-option>
                                </a-select>
                            </div>
                            <div v-if="getSelectedProp('linkType', 'none') === 'systemPage'" class="config-row">
                                <label>系统页面</label>
                                <a-select :value="getSelectedProp('linkTarget')" placeholder="选择系统页面" style="width: 100%" @change="value => updateSelectedLinkTarget('systemPage', value)">
                                    <a-select-option v-for="page in systemPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                </a-select>
                            </div>
                            <div v-if="getSelectedProp('linkType', 'none') === 'customPage'" class="config-row">
                                <label>自定义页面</label>
                                <a-select :value="getSelectedProp('linkTarget')" placeholder="选择自定义页面" style="width: 100%" @change="value => updateSelectedLinkTarget('customPage', value)">
                                    <a-select-option v-for="page in customPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                </a-select>
                            </div>
                        </template>

                        <template v-else-if="selectedComponent.type === 'imageBanner'">
                            <div class="config-row">
                                <label>每行显示</label>
                                <a-radio-group :value="getSelectedProp('columns', 1)" @change="e => updateSelectedProp('columns', e.target.value)">
                                    <a-radio-button :value="1">1张</a-radio-button>
                                    <a-radio-button :value="2">2张</a-radio-button>
                                    <a-radio-button :value="3">3张</a-radio-button>
                                </a-radio-group>
                            </div>
                            <div class="config-row">
                                <label>圆角</label>
                                <a-input-number :value="getSelectedProp('radius', 16)" :min="0" :max="32" @change="value => updateSelectedProp('radius', value)" />
                            </div>
                            <div class="config-row">
                                <label>图片间距</label>
                                <a-input-number :value="getSelectedProp('gap', 8)" :min="0" :max="24" @change="value => updateSelectedProp('gap', value)" />
                            </div>
                            <div class="config-row">
                                <label>图片比例</label>
                                <a-select :value="getSelectedProp('aspectRatio', '16:9')" style="width: 100%" @change="value => updateSelectedProp('aspectRatio', value)">
                                    <a-select-option value="16:9">16:9</a-select-option>
                                    <a-select-option value="3:1">3:1</a-select-option>
                                    <a-select-option value="1:1">1:1</a-select-option>
                                    <a-select-option value="4:3">4:3</a-select-option>
                                </a-select>
                            </div>
                            <div class="config-row config-row-top">
                                <label>图片列表</label>
                                <div class="banner-editor">
                                    <a-button type="primary" icon="plus" block @click="openImageBannerPicker()">添加图片</a-button>
                                    <div v-if="imageBannerItems.length" class="banner-editor-list">
                                        <div v-for="(banner, index) in imageBannerItems" :key="index" class="banner-editor-item">
                                            <div class="banner-editor-preview">
                                                <img v-if="banner.imageUrl" :src="resolveEditorImage(banner.imageUrl)" />
                                                <span v-else>未选图</span>
                                            </div>
                                            <div class="banner-editor-main">
                                                <div class="banner-editor-head">
                                                    <span>图片 {{ index + 1 }}</span>
                                                    <div>
                                                        <a-button size="small" icon="arrow-up" :disabled="index === 0" @click="moveImageBannerItem(index, -1)" />
                                                        <a-button size="small" icon="arrow-down" :disabled="index === imageBannerItems.length - 1" @click="moveImageBannerItem(index, 1)" />
                                                        <a-button size="small" icon="delete" @click="removeImageBannerItem(index)" />
                                                    </div>
                                                </div>
                                                <div class="banner-editor-row">
                                                    <a-input :value="banner.imageUrl" placeholder="图片地址" @change="e => updateImageBannerItem(index, 'imageUrl', e.target.value)" />
                                                    <a-button icon="picture" @click="openImageBannerPicker(index)">替换</a-button>
                                                </div>
                                                <div class="banner-editor-row">
                                                    <a-select :value="banner.linkType || 'none'" style="width: 120px" @change="value => updateImageBannerItem(index, 'linkType', value)">
                                                        <a-select-option value="none">不跳转</a-select-option>
                                                        <a-select-option value="systemPage">系统页面</a-select-option>
                                                        <a-select-option value="customPage">自定义页面</a-select-option>
                                                    </a-select>
                                                    <a-select v-if="banner.linkType === 'systemPage'" :value="banner.linkTarget" placeholder="选择系统页面" style="flex: 1" @change="value => updateImageBannerLinkTarget(index, 'systemPage', value)">
                                                        <a-select-option v-for="page in systemPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                                    </a-select>
                                                    <a-select v-if="banner.linkType === 'customPage'" :value="banner.linkTarget" placeholder="选择自定义页面" style="flex: 1" @change="value => updateImageBannerLinkTarget(index, 'customPage', value)">
                                                        <a-select-option v-for="page in customPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                                    </a-select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <a-empty v-else description="请添加图片" />
                                </div>
                            </div>
                        </template>

                        <template v-else-if="selectedComponent.type === 'couponGrid'">
                            <div class="config-row">
                                <label>标题</label>
                                <a-input :value="getSelectedProp('title')" @change="e => updateSelectedProp('title', e.target.value)" />
                            </div>
                            <div class="config-row config-row-top">
                                <label>系统优惠券</label>
                                <div class="coupon-editor-list">
                                    <a-select
                                        showSearch
                                        allowClear
                                        :value="undefined"
                                        :loading="couponLoading"
                                        placeholder="选择优惠券添加到领券区"
                                        optionFilterProp="children"
                                        style="width: 100%"
                                        @dropdownVisibleChange="handleCouponDropdown"
                                        @change="addCouponItem"
                                    >
                                        <a-select-option v-for="item in couponOptions" :key="item.couponId" :value="item.couponId">
                                            {{ getCouponOptionLabel(item) }}
                                        </a-select-option>
                                    </a-select>
                                    <div v-for="(coupon, index) in couponItems" :key="coupon.couponId || index" class="coupon-editor-item">
                                        <div class="coupon-editor-main">
                                            <span>{{ coupon.couponName || coupon.title || '优惠券' }}</span>
                                            <em>{{ coupon.couponValue || `¥${coupon.amount || coupon.value || 0}` }} · {{ coupon.couponLabel || getCouponLabel(coupon) }}</em>
                                        </div>
                                        <div class="coupon-editor-actions">
                                            <a-button size="small" icon="arrow-up" :disabled="index === 0" @click="moveCouponItem(index, -1)" />
                                            <a-button size="small" icon="arrow-down" :disabled="index === couponItems.length - 1" @click="moveCouponItem(index, 1)" />
                                            <a-button size="small" icon="delete" type="danger" @click="removeCouponItem(index)" />
                                        </div>
                                    </div>
                                    <a-empty v-if="!couponItems.length" description="请选择要展示的系统优惠券" />
                                </div>
                            </div>
                        </template>

                        <template v-else-if="selectedComponent.type === 'promotionCard'">
                            <div class="config-row">
                                <label>标题</label>
                                <a-input :value="getSelectedProp('title')" @change="e => updateSelectedProp('title', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>副标题</label>
                                <a-input :value="getSelectedProp('subtitle')" @change="e => updateSelectedProp('subtitle', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>按钮文字</label>
                                <a-input :value="getSelectedProp('buttonText')" @change="e => updateSelectedProp('buttonText', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>图片地址</label>
                                <a-input-group compact>
                                    <a-input :value="getSelectedProp('imageUrl')" style="width: calc(100% - 88px)" @change="e => updateSelectedProp('imageUrl', e.target.value)" />
                                    <a-button icon="picture" @click="openComponentImagePicker('imageUrl')">选择</a-button>
                                </a-input-group>
                            </div>
                            <div class="config-row">
                                <label>卡片跳转</label>
                                <a-select :value="getSelectedProp('linkType', 'none')" style="width: 100%" @change="value => updateSelectedLinkType(value)">
                                    <a-select-option value="none">不跳转</a-select-option>
                                    <a-select-option value="systemPage">系统页面</a-select-option>
                                    <a-select-option value="customPage">自定义页面</a-select-option>
                                </a-select>
                            </div>
                            <div v-if="getSelectedProp('linkType', 'none') === 'systemPage'" class="config-row">
                                <label>系统页面</label>
                                <a-select :value="getSelectedProp('linkTarget')" placeholder="选择系统页面" style="width: 100%" @change="value => updateSelectedLinkTarget('systemPage', value)">
                                    <a-select-option v-for="page in systemPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                </a-select>
                            </div>
                            <div v-if="getSelectedProp('linkType', 'none') === 'customPage'" class="config-row">
                                <label>自定义页面</label>
                                <a-select :value="getSelectedProp('linkTarget')" placeholder="选择自定义页面" style="width: 100%" @change="value => updateSelectedLinkTarget('customPage', value)">
                                    <a-select-option v-for="page in customPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                </a-select>
                            </div>
                        </template>

                        <template v-else-if="selectedComponent.type === 'richText'">
                            <div class="config-row">
                                <label>标题</label>
                                <a-input :value="getSelectedProp('title')" @change="e => updateSelectedProp('title', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>文本内容</label>
                                <a-textarea :value="getSelectedProp('content')" :auto-size="{ minRows: 4, maxRows: 8 }" @change="e => updateSelectedProp('content', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>文字对齐</label>
                                <a-radio-group :value="getSelectedProp('align', 'left')" @change="e => updateSelectedProp('align', e.target.value)">
                                    <a-radio-button value="left">左对齐</a-radio-button>
                                    <a-radio-button value="center">居中</a-radio-button>
                                    <a-radio-button value="right">右对齐</a-radio-button>
                                </a-radio-group>
                            </div>
                            <div class="config-row">
                                <label>文本跳转</label>
                                <a-select :value="getSelectedProp('linkType', 'none')" style="width: 100%" @change="value => updateSelectedLinkType(value)">
                                    <a-select-option value="none">不跳转</a-select-option>
                                    <a-select-option value="systemPage">系统页面</a-select-option>
                                    <a-select-option value="customPage">自定义页面</a-select-option>
                                </a-select>
                            </div>
                            <div v-if="getSelectedProp('linkType', 'none') === 'systemPage'" class="config-row">
                                <label>系统页面</label>
                                <a-select :value="getSelectedProp('linkTarget')" placeholder="选择系统页面" style="width: 100%" @change="value => updateSelectedLinkTarget('systemPage', value)">
                                    <a-select-option v-for="page in systemPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                </a-select>
                            </div>
                            <div v-if="getSelectedProp('linkType', 'none') === 'customPage'" class="config-row">
                                <label>自定义页面</label>
                                <a-select :value="getSelectedProp('linkTarget')" placeholder="选择自定义页面" style="width: 100%" @change="value => updateSelectedLinkTarget('customPage', value)">
                                    <a-select-option v-for="page in customPageOptions" :key="page.value" :value="page.value">{{ page.label }}</a-select-option>
                                </a-select>
                            </div>
                        </template>

                        <template v-else-if="selectedComponent.type === 'blankSpace'">
                            <div class="config-row">
                                <label>高度</label>
                                <a-input-number :value="getSelectedProp('height', 24)" :min="0" :max="120" @change="value => updateSelectedProp('height', value)" />
                            </div>
                        </template>

                        <template v-else-if="selectedComponent.type === 'goodsList'">
                            <div class="config-row">
                                <label>标题</label>
                                <a-input :value="getSelectedProp('title')" @change="e => updateSelectedProp('title', e.target.value)" />
                            </div>
                            <div class="config-row">
                                <label>显示数量</label>
                                <a-input-number :value="getSelectedProp('showCount', 6)" :min="2" :max="20" @change="value => updateSelectedProp('showCount', value)" />
                            </div>
                            <div class="config-row">
                                <label>数据来源</label>
                                <a-radio-group :value="selectedGoodsDataSourceMode" @change="e => updateSelectedDataSource('mode', e.target.value)">
                                    <a-radio-button value="category">分类</a-radio-button>
                                    <a-radio-button value="manual">手选</a-radio-button>
                                </a-radio-group>
                            </div>
                            <div v-if="selectedGoodsDataSourceMode === 'category'" class="config-row">
                                <label>商品分类</label>
                                <a-select :value="selectedDataSource.categoryId" placeholder="选择分类" style="width: 100%" @change="value => updateSelectedDataSource('categoryId', value)">
                                    <a-select-option v-for="item in categoryList" :key="item.value" :value="item.value">{{ item.text || item.label }}</a-select-option>
                                </a-select>
                            </div>
                            <div v-if="selectedGoodsDataSourceMode === 'manual'" class="config-row config-row-top">
                                <label>手选商品</label>
                                <div class="selected-goods-editor">
                                    <a-button type="primary" icon="plus" block @click="openGoodsPicker">添加商品</a-button>
                                    <div v-if="selectedGoodsItems.length" class="selected-goods-list">
                                        <div v-for="(goods, index) in selectedGoodsItems" :key="goods.goodsId || index" class="selected-goods-card">
                                            <div class="selected-goods-image">
                                                <img v-if="goods.image || goods.imageUrl" :src="resolveEditorImage(goods.image || goods.imageUrl)" />
                                                <a-icon v-else type="picture" />
                                            </div>
                                            <div class="selected-goods-info">
                                                <div class="selected-goods-name">{{ goods.goodsName || '未命名商品' }}</div>
                                                <div class="selected-goods-no">{{ goods.goodsNo || goods.goodsId }}</div>
                                                <div class="selected-goods-price">¥{{ getGoodsPrice(goods) }}</div>
                                            </div>
                                            <div class="selected-goods-actions">
                                                <a-button size="small" icon="arrow-up" :disabled="index === 0" @click="moveGoodsItem(index, -1)" />
                                                <a-button size="small" icon="arrow-down" :disabled="index === selectedGoodsItems.length - 1" @click="moveGoodsItem(index, 1)" />
                                                <a-button size="small" icon="delete" @click="removeGoodsItem(index)" />
                                            </div>
                                        </div>
                                    </div>
                                    <a-empty v-else description="请选择要展示的商品" />
                                </div>
                            </div>
                        </template>
                    </div>

                    <div class="editor-actions">
                        <a-button type="primary" @click="saveDraft">保存草稿</a-button>
                    </div>
                </div>
            </div>
        </a-spin>
        <mall-v2-image-picker
            :visible.sync="imagePickerVisible"
            :multiple="imagePickerMultiple"
            :value="imagePickerValue"
            @select="handleImagePickerSelect"
        />
        <mall-v2-goods-picker
            :visible.sync="goodsPickerVisible"
            :value="goodsPickerValue"
            :storeShopId="storeShopId"
            :categoryList="categoryList"
            @select="applyGoodsSelection"
        />
        <a-modal
            :visible="customPageModalVisible"
            :title="customPageModalMode === 'rename' ? '重命名自定义页' : '新增自定义页'"
            @cancel="customPageModalVisible = false"
            @ok="submitCustomPage"
        >
            <a-input v-model="customPageForm.pageName" placeholder="请输入页面名称" />
        </a-modal>
        <a-modal
            :visible="copyModalVisible"
            title="复制其他商城装修"
            :confirmLoading="copying"
            @cancel="copyModalVisible = false"
            @ok="submitCopyDecorate"
        >
            <div class="copy-modal-tip">
                复制后会覆盖当前商城的装修草稿，并清空当前商城已发布版本；复制完成后请预览确认再发布。
            </div>
            <div class="copy-modal-field">
                <label>来源商城</label>
                <a-select
                    showSearch
                    allowClear
                    :filterOption="false"
                    :value="copyForm.sourceStoreShopId"
                    :loading="copySourceLoading"
                    placeholder="请选择已装修的商城"
                    style="width: 100%"
                    @search="loadCopySourceStores"
                    @dropdownVisibleChange="handleCopySourceDropdown"
                    @change="value => copyForm.sourceStoreShopId = value"
                >
                    <a-select-option v-for="item in copySourceList" :key="item.value" :value="item.value">
                        {{ item.label }}
                    </a-select-option>
                </a-select>
            </div>
        </a-modal>
    </div>
</template>

<script>
import MallV2ImagePicker from './components/MallV2ImagePicker'
import MallV2GoodsPicker from './components/MallV2GoodsPicker'
import MallV2Preview from './components/MallV2Preview'
import {
    addMallCustomPageV2,
    applyMallTemplateV2,
    copyMallDecorateV2FromStore,
    deleteMallCustomPageV2,
    enableMallDecorateV2,
    getMallPageV2,
    initMallDecorateV2,
    listMallPageV2,
    listMallTemplateV2,
    listMallThemeV2,
    publishMallPageV2,
    renameMallCustomPageV2,
    saveMallPageV2Draft,
    selectMallDecorateCategory,
    selectMallDecorateCopySourceStore,
    selectMallDecorateCoupon,
    selectMallDecorateGoods
} from '@/api/mallDecorateV2'
import { image } from '@/utils/Tool'
import { V2_CONTROL_PRESETS, cloneV2Component } from './schema/mallV2Controls'

export default {
    name: 'MallDecorateV2',
    components: { MallV2ImagePicker, MallV2GoodsPicker, MallV2Preview },
    data() {
        return {
            loading: false,
            storeShopId: null,
            useDecorateV2: false,
            pageList: [],
            templateList: [],
            themeList: [],
            categoryList: [],
            goodsPickerVisible: false,
            goodsPickerValue: [],
            couponOptions: [],
            couponLoading: false,
            currentPage: null,
            draftContent: '{}',
            selectedComponentIndex: -1,
            imagePickerVisible: false,
            imagePickerTarget: null,
            imagePickerValue: '',
            imagePickerMultiple: false,
            copyModalVisible: false,
            copySourceList: [],
            copySourceLoading: false,
            copying: false,
            copyForm: {
                sourceStoreShopId: ''
            },
            customPageModalVisible: false,
            customPageModalMode: 'add',
            customPageForm: {
                pageType: '',
                pageName: ''
            },
            controlPresets: V2_CONTROL_PRESETS
        }
    },
    computed: {
        systemPageOptions() {
            return [
                { label: '商城首页', value: 'pages/index/index' },
                { label: '商品分类', value: 'pages/cate/cate' },
                { label: '购物车', value: 'pages/cart/cart' },
                { label: '会员中心', value: 'pages/user/user' },
                { label: '会员开卡', value: 'pages/open/open-card' },
                { label: '搜索商品', value: 'pages/search/search' },
                { label: '我的订单', value: 'pages/order/order-list' },
                { label: '卡券中心', value: 'pages/user/user-coupon' },
                { label: '会员充值', value: 'pages/user/recharge' }
            ]
        },
        navIconOptions() {
            return [
                { label: '新品', value: 'new', text: '新' },
                { label: '热卖', value: 'hot', text: '热' },
                { label: '分类', value: 'category', text: '类' },
                { label: '优惠券', value: 'coupon', text: '券' },
                { label: '会员', value: 'vip', text: '会' },
                { label: '订单', value: 'order', text: '单' },
                { label: '购物车', value: 'cart', text: '车' },
                { label: '充值', value: 'recharge', text: '充' },
                { label: '门店', value: 'store', text: '店' },
                { label: '客服', value: 'service', text: '客' }
            ]
        },
        parsedContent() {
            if (!this.draftContent) {
                return null
            }
            try {
                return JSON.parse(this.draftContent)
            } catch (e) {
                return null
            }
        },
        draftComponents() {
            const content = this.parsedContent
            return content && Array.isArray(content.components) ? content.components : []
        },
        isComponentPage() {
            return !this.currentPage || this.currentPage.pageType === 'HOME' || this.isCustomPage(this.currentPage.pageType)
        },
        currentPageType() {
            return this.currentPage && this.currentPage.pageType ? this.currentPage.pageType : 'HOME'
        },
        activeControlPresets() {
            const pageType = this.isCustomPage(this.currentPageType) ? 'HOME' : this.currentPageType
            return this.controlPresets.filter(item => !item.pageTypes || item.pageTypes.indexOf(pageType) !== -1)
        },
        customPageOptions() {
            return this.pageList.filter(item => this.isCustomPage(item.pageType)).map(item => ({
                label: item.pageName || '自定义页面',
                value: `pages_v2/topic/topic?pageType=${item.pageType}`
            }))
        },
        selectedComponent() {
            return this.isComponentPage && this.selectedComponentIndex >= 0 ? this.draftComponents[this.selectedComponentIndex] : null
        },
        selectedDataSource() {
            return this.selectedComponent && this.selectedComponent.dataSource ? this.selectedComponent.dataSource : {}
        },
        selectedGoodsDataSourceMode() {
            return this.selectedDataSource.mode === 'manual' ? 'manual' : 'category'
        },
        firstSelectedImage() {
            const items = this.getSelectedProp('items', [])
            return items && items.length > 0 ? items[0].imageUrl : ''
        },
        swiperItems() {
            const items = this.getSelectedProp('items', [])
            return Array.isArray(items) ? items : []
        },
        navItems() {
            const items = this.getSelectedProp('items', [])
            return Array.isArray(items) ? items : []
        },
        couponItems() {
            const items = this.getSelectedProp('items', [])
            return Array.isArray(items) ? items : []
        },
        imageBannerItems() {
            const items = this.getSelectedProp('items', [])
            if (Array.isArray(items) && items.length) {
                return items
            }
            const imageUrl = this.getSelectedProp('imageUrl', '')
            return imageUrl ? [{
                imageUrl,
                linkType: this.getSelectedProp('linkType', 'none'),
                linkTarget: this.getSelectedProp('linkTarget', ''),
                linkName: this.getSelectedProp('linkName', '')
            }] : []
        },
        selectedGoodsItems() {
            const dataSource = this.selectedDataSource
            const snapshots = Array.isArray(dataSource.goodsSnapshots) ? dataSource.goodsSnapshots : []
            if (snapshots.length) {
                return snapshots
            }
            const goodsIds = Array.isArray(dataSource.goodsIds) ? dataSource.goodsIds : []
            return goodsIds.map(goodsId => ({ goodsId }))
        },
        openCardFeatureText() {
            const items = this.getPageConfig('featureList', [])
            return Array.isArray(items) ? items.map(item => `${item.title || item.name || ''}|${item.subtitle || item.desc || ''}|${item.icon || ''}`).join('\n') : ''
        },
        userCenterFeatureText() {
            const items = this.getPageConfig('featureList', [])
            return Array.isArray(items) ? items.map(item => `${item.title || item.name || ''}|${item.subtitle || item.desc || ''}|${item.icon || ''}`).join('\n') : ''
        },
        activeThemeCode() {
            const content = this.parsedContent
            return content && content.theme ? content.theme : ''
        },
        homeTitleMode() {
            return this.getPageConfig('titleMode', 'shopName') === 'custom' ? 'custom' : 'shopName'
        },
        homeStoreShopName() {
            return this.getPageConfig('storeShopName', '') || '商城首页'
        },
        homeTitleText() {
            return this.homeTitleMode === 'shopName' ? this.homeStoreShopName : this.getPageConfig('pageTitle', this.homeStoreShopName)
        }
    },
    methods: {
        loadAll() {
            if (!this.storeShopId) {
                this.$message.warning('缺少店铺ID')
                return
            }
            this.loading = true
            return initMallDecorateV2(this.storeShopId)
                .then(() => Promise.all([listMallPageV2(this.storeShopId), listMallTemplateV2(), listMallThemeV2()]))
                .then(([pageRes, templateRes, themeRes]) => {
                    if (pageRes.success) {
                        this.pageList = pageRes.result || []
                        if (!this.currentPage && this.pageList.length > 0) {
                            this.selectPage(this.pageList[0].pageType)
                        }
                    }
                    if (templateRes.success) {
                        this.templateList = templateRes.result || []
                    }
                    if (themeRes.success) {
                        this.themeList = themeRes.result || []
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        },
        loadSelectors() {
            selectMallDecorateCategory()
                .then(res => {
                    if (res.success) {
                        this.categoryList = res.result || []
                    }
                })
                .catch(error => {
                    this.categoryList = []
                    this.$message.warning(this.getErrorMessage(error, '商品分类选择器加载失败，可先继续编辑页面'))
                })
        },
        isCustomPage(pageType) {
            return !!pageType && pageType.indexOf('CUSTOM_') === 0
        },
        getPageLabel(page) {
            const type = page && page.pageType ? page.pageType : page
            const labelMap = {
                HOME: '首页装修',
                PRODUCT_DETAIL: '商品详情页',
                OPEN_CARD: '开卡页面',
                USER_CENTER: '个人中心',
                CART: '购物车'
            }
            return labelMap[type] || (page && page.pageName) || type || '未命名页面'
        },
        getPageDesc(page) {
            const type = page && page.pageType ? page.pageType : page
            const descMap = {
                HOME: '商城首页，主要展示轮播、导航、商品和活动',
                PRODUCT_DETAIL: '商品详情页，配置库存展示、页面视觉等详情页能力',
                OPEN_CARD: '会员开卡页，配置开卡权益和提示',
                USER_CENTER: '会员中心页，配置会员说明和功能入口',
                CART: '购物车页，配置空购物车提示和按钮'
            }
            return descMap[type] || (this.isCustomPage(type) ? '自定义专题页，支持插入首页控件' : type || '')
        },
        getThemeCode(theme) {
            return theme && (theme.themeCode || theme.code || theme.styleKey) ? (theme.themeCode || theme.code || theme.styleKey) : ''
        },
        getThemeName(theme) {
            return theme && (theme.themeName || theme.name) ? (theme.themeName || theme.name) : this.getThemeCode(theme)
        },
        getThemePrimaryColor(theme) {
            const code = this.getThemeCode(theme)
            const fallback = {
                simple: '#222222',
                fashion_women: '#D85C8A',
                brand: '#111111',
                promotion: '#E53935'
            }
            return theme && theme.primaryColor ? theme.primaryColor : (fallback[code] || '#D64545')
        },
        applyTheme(theme) {
            const themeCode = this.getThemeCode(theme)
            if (!themeCode) {
                return
            }
            this.$confirm({
                title: `应用整店风格：${this.getThemeName(theme)}`,
                content: '将统一更新固定页面和自定义页面的草稿主题与主色，保持商城风格一致。',
                onOk: () => this.applyThemeToAllPages(theme)
            })
        },
        applyThemeToAllPages(theme) {
            const pages = this.pageList.length ? this.pageList : [{ pageType: this.currentPageType }]
            const currentType = this.currentPage && this.currentPage.pageType ? this.currentPage.pageType : ''
            const currentModel = currentType ? this.parseDraft() : null
            if (currentType && !currentModel) {
                return Promise.reject(new Error('JSON 格式不正确'))
            }
            this.loading = true
            const tasks = pages.map(page => {
                const pageType = page.pageType
                if (pageType === currentType) {
                    const model = this.patchThemeToModel(currentModel, theme, pageType)
                    return this.saveThemeDraft(pageType, model)
                }
                return getMallPageV2(this.storeShopId, pageType).then(res => {
                    const draftContent = res.success && res.result && res.result.draftContent ? res.result.draftContent : '{}'
                    let model = {}
                    try {
                        model = JSON.parse(draftContent)
                    } catch (e) {
                        model = {}
                    }
                    this.patchThemeToModel(model, theme, pageType)
                    return this.saveThemeDraft(pageType, model)
                })
            })
            return Promise.all(tasks)
                .then(() => {
                    this.$message.success('整店风格已应用到所有页面草稿')
                    if (currentType) {
                        this.selectPage(currentType)
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        },
        patchThemeToModel(model, theme, pageType) {
            const primaryColor = this.getThemePrimaryColor(theme)
            model.theme = this.getThemeCode(theme)
            model.pageType = model.pageType || pageType
            if (!model.config) {
                model.config = {}
            }
            model.config.primaryColor = primaryColor
            model.config.buttonColor = primaryColor
            if (theme.backgroundColor) {
                model.config.backgroundColor = theme.backgroundColor
            }
            if (theme.priceColor) {
                model.config.priceColor = theme.priceColor
            }
            return model
        },
        saveThemeDraft(pageType, model) {
            return saveMallPageV2Draft({
                storeShopId: this.storeShopId,
                pageType,
                draftContent: JSON.stringify(model, null, 2)
            })
        },
        selectPage(pageType) {
            this.loading = true
            return getMallPageV2(this.storeShopId, pageType)
                .then(res => {
                    if (res.success) {
                        this.currentPage = res.result
                        this.draftContent = this.currentPage && this.currentPage.draftContent ? this.currentPage.draftContent : '{}'
                        this.selectedComponentIndex = -1
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        },
        parseDraft() {
            try {
                const model = this.draftContent ? JSON.parse(this.draftContent) : {}
                if (!model.pageType && this.currentPageType) {
                    model.pageType = this.currentPageType
                }
                if (!model.config) {
                    model.config = {}
                }
                if (this.isComponentPage && !Array.isArray(model.components)) {
                    model.components = []
                }
                return model
            } catch (e) {
                this.$message.error('JSON 格式不正确，请先修正后再操作控件')
                return null
            }
        },
        saveModel(model, keepSelection = true) {
            this.draftContent = JSON.stringify(model, null, 2)
            if (!keepSelection) {
                this.selectedComponentIndex = -1
            }
        },
        updateDraft(mutator) {
            const model = this.parseDraft()
            if (!model) {
                return false
            }
            mutator(model)
            this.saveModel(model)
            return true
        },
        insertPreset(preset) {
            if (!this.isComponentPage) {
                this.$message.warning('当前页面使用专用配置项，不支持插入首页控件')
                return
            }
            const component = preset && typeof preset.create === 'function' ? preset.create() : null
            if (!component) {
                return
            }
            this.updateDraft(model => {
                model.components.push(component)
                this.selectedComponentIndex = model.components.length - 1
            })
        },
        selectComponent(index) {
            if (!this.isComponentPage) {
                return
            }
            this.selectedComponentIndex = index
        },
        duplicateComponent(index) {
            this.updateDraft(model => {
                const source = model.components[index]
                if (!source) {
                    return
                }
                const copy = cloneV2Component(source)
                copy.id = `${copy.type || 'component'}_${Date.now().toString(36)}`
                model.components.splice(index + 1, 0, copy)
                this.selectedComponentIndex = index + 1
            })
        },
        moveComponent(index, delta) {
            this.updateDraft(model => {
                const target = index + delta
                if (target < 0 || target >= model.components.length) {
                    return
                }
                const list = model.components
                const current = list[index]
                list.splice(index, 1)
                list.splice(target, 0, current)
                this.selectedComponentIndex = target
            })
        },
        removeComponent(index) {
            this.updateDraft(model => {
                model.components.splice(index, 1)
                if (this.selectedComponentIndex === index) {
                    this.selectedComponentIndex = -1
                } else if (this.selectedComponentIndex > index) {
                    this.selectedComponentIndex -= 1
                }
            })
        },
        getComponentLabel(item) {
            const preset = this.controlPresets.find(presetItem => presetItem.type === item.type)
            if (preset) {
                return preset.label
            }
            return item.type || '未命名控件'
        },
        getComponentSummary(item) {
            if (!item) {
                return ''
            }
            const parts = [item.type]
            if (item.id) {
                parts.push(item.id)
            }
            if (item.visible === false) {
                parts.push('隐藏')
            }
            return parts.join(' · ')
        },
        getSelectedProp(key, defaultValue = '') {
            const component = this.selectedComponent
            return component && component.props && component.props[key] !== undefined ? component.props[key] : defaultValue
        },
        resolveEditorImage(value) {
            if (!value) {
                return ''
            }
            if (/^(https?:)?\/\//.test(value) || value.indexOf('data:') === 0 || value.indexOf('/img/') === 0 || value.indexOf('/static/') === 0) {
                return value
            }
            return image(value)
        },
        updateSelectedComponent(mutator) {
            if (this.selectedComponentIndex < 0) {
                return
            }
            this.updateDraft(model => {
                const component = model.components[this.selectedComponentIndex]
                if (component) {
                    mutator(component)
                }
            })
        },
        updateSelectedField(key, value) {
            this.updateSelectedComponent(component => {
                component[key] = value
            })
        },
        updateSelectedProp(key, value) {
            this.updateSelectedComponent(component => {
                if (!component.props) {
                    component.props = {}
                }
                component.props[key] = value
            })
        },
        updateSelectedDataSource(key, value) {
            this.updateSelectedComponent(component => {
                if (!component.dataSource) {
                    component.dataSource = {}
                }
                component.dataSource[key] = value
                if (key === 'mode' && value !== 'mock') {
                    component.dataSource.mock = false
                }
            })
        },
        updateSelectedImage(imageUrl) {
            this.updateSelectedComponent(component => {
                if (!component.props) {
                    component.props = {}
                }
                const items = Array.isArray(component.props.items) ? component.props.items : []
                if (items.length === 0) {
                    items.push({ imageUrl, linkType: 'none' })
                } else {
                    items[0].imageUrl = imageUrl
                }
                component.props.items = items
            })
        },
        updateSwiperLink(index, key, value) {
            this.updateSelectedComponent(component => {
                const items = component.props && Array.isArray(component.props.items) ? component.props.items : []
                const item = items[index]
                if (!item) {
                    return
                }
                item[key] = value
                if (key === 'linkType' && value === 'none') {
                    item.linkTarget = ''
                    item.linkName = ''
                }
            })
        },
        updateSwiperLinkTarget(index, linkType, value) {
            const options = linkType === 'systemPage' ? this.systemPageOptions : this.customPageOptions
            const target = options.find(item => item.value === value)
            this.updateSelectedComponent(component => {
                const items = component.props && Array.isArray(component.props.items) ? component.props.items : []
                const item = items[index]
                if (!item) {
                    return
                }
                item.linkType = linkType
                item.linkTarget = value
                item.linkName = target ? target.label : value
            })
        },
        updateSearchLinkType(value) {
            this.updateSelectedComponent(component => {
                if (!component.props) {
                    component.props = {}
                }
                component.props.linkType = value
                if (value === 'none') {
                    component.props.linkTarget = ''
                    component.props.linkName = ''
                } else if (value === 'systemPage') {
                    const defaultTarget = component.props.linkTarget || 'pages/search/search'
                    const target = this.systemPageOptions.find(item => item.value === defaultTarget)
                    component.props.linkTarget = defaultTarget
                    component.props.linkName = target ? target.label : '搜索商品'
                } else if (value === 'customPage') {
                    component.props.linkTarget = ''
                    component.props.linkName = ''
                }
            })
        },
        updateSearchLinkTarget(linkType, value) {
            const options = linkType === 'systemPage' ? this.systemPageOptions : this.customPageOptions
            const target = options.find(item => item.value === value)
            this.updateSelectedComponent(component => {
                if (!component.props) {
                    component.props = {}
                }
                component.props.linkType = linkType
                component.props.linkTarget = value
                component.props.linkName = target ? target.label : value
            })
        },
        updateSelectedLinkType(value) {
            this.updateSelectedComponent(component => {
                if (!component.props) {
                    component.props = {}
                }
                component.props.linkType = value
                component.props.linkTarget = ''
                component.props.linkName = ''
            })
        },
        updateSelectedLinkTarget(linkType, value) {
            const options = linkType === 'systemPage' ? this.systemPageOptions : this.customPageOptions
            const target = options.find(item => item.value === value)
            this.updateSelectedComponent(component => {
                if (!component.props) {
                    component.props = {}
                }
                component.props.linkType = linkType
                component.props.linkTarget = value
                component.props.linkName = target ? target.label : value
            })
        },
        openPageImagePicker(configKey) {
            this.imagePickerTarget = {
                scope: 'page',
                key: configKey
            }
            this.imagePickerValue = this.getPageConfig(configKey, '')
            this.imagePickerMultiple = false
            this.imagePickerVisible = true
        },
        openComponentImagePicker(propKey, options = {}) {
            if (!this.selectedComponent) {
                return
            }
            this.imagePickerTarget = {
                scope: 'component',
                key: propKey,
                componentIndex: this.selectedComponentIndex,
                action: options.action || 'set',
                index: options.index
            }
            this.imagePickerMultiple = propKey === 'swiperItems' && options.action !== 'replace'
            if (propKey === 'swiperItems' && options.action === 'replace') {
                const item = this.swiperItems[options.index]
                this.imagePickerValue = item && item.imageUrl ? item.imageUrl : ''
            } else {
                this.imagePickerValue = propKey === 'swiperItems' ? '' : this.getSelectedProp(propKey, '')
            }
            this.imagePickerVisible = true
        },
        openImageBannerPicker(index) {
            this.imagePickerTarget = {
                scope: 'imageBanner',
                action: index >= 0 ? 'replace' : 'add',
                index
            }
            if (index >= 0) {
                const item = this.imageBannerItems[index]
                this.imagePickerValue = item && item.imageUrl ? item.imageUrl : ''
                this.imagePickerMultiple = false
            } else {
                this.imagePickerValue = ''
                this.imagePickerMultiple = true
            }
            this.imagePickerVisible = true
        },
        handleImagePickerSelect(selected) {
            const target = this.imagePickerTarget
            if (!target) {
                return
            }
            if (target.scope === 'page') {
                this.updatePageConfig(target.key, selected.path)
            } else if (target.scope === 'component' && target.key === 'swiperItems') {
                this.applySwiperImages(selected, target)
            } else if (target.scope === 'imageBanner') {
                this.applyImageBannerImages(selected, target)
            } else if (target.scope === 'component') {
                this.updateSelectedProp(target.key, selected.path)
            } else if (target.scope === 'navIcon') {
                this.updateNavItem(target.index, 'iconImage', selected.path)
                this.updateNavItem(target.index, 'iconType', 'image')
            }
            this.imagePickerTarget = null
            this.imagePickerMultiple = false
        },
        applySwiperImages(selected, target = {}) {
            const selectedList = Array.isArray(selected) ? selected : [selected]
            const paths = selectedList.map(item => item.path).filter(Boolean)
            if (!paths.length) {
                return
            }
            this.updateSelectedComponent(component => {
                if (!component.props) {
                    component.props = {}
                }
                const currentItems = Array.isArray(component.props.items) ? component.props.items : []
                if (target.action === 'replace' && target.index >= 0 && currentItems[target.index]) {
                    currentItems[target.index].imageUrl = paths[0]
                    component.props.items = currentItems
                    return
                }
                const currentPaths = currentItems.map(item => item.imageUrl)
                paths.forEach(path => {
                    if (currentPaths.indexOf(path) === -1) {
                        currentItems.push({ imageUrl: path, linkType: 'none' })
                    }
                })
                component.props.items = currentItems
            })
        },
        updateSwiperImage(index, imageUrl) {
            this.updateSelectedComponent(component => {
                const items = component.props && Array.isArray(component.props.items) ? component.props.items : []
                const item = items[index]
                if (!item) {
                    return
                }
                item.imageUrl = imageUrl
                component.props.items = items
            })
        },
        removeSwiperItem(index) {
            this.updateSelectedComponent(component => {
                const items = component.props && Array.isArray(component.props.items) ? component.props.items : []
                items.splice(index, 1)
                component.props.items = items
            })
        },
        moveSwiperItem(index, delta) {
            this.updateSelectedComponent(component => {
                const items = component.props && Array.isArray(component.props.items) ? component.props.items : []
                const target = index + delta
                if (target < 0 || target >= items.length) {
                    return
                }
                const current = items[index]
                items.splice(index, 1)
                items.splice(target, 0, current)
                component.props.items = items
            })
        },
        normalizeImageBannerProps(component) {
            if (!component.props) {
                component.props = {}
            }
            let items = Array.isArray(component.props.items) ? component.props.items : []
            if (!items.length && component.props.imageUrl) {
                items = [{
                    imageUrl: component.props.imageUrl,
                    linkType: component.props.linkType || 'none',
                    linkTarget: component.props.linkTarget || '',
                    linkName: component.props.linkName || ''
                }]
            }
            component.props.items = items
            return items
        },
        applyImageBannerImages(selected, target = {}) {
            const selectedList = Array.isArray(selected) ? selected : [selected]
            const paths = selectedList.map(item => item.path).filter(Boolean)
            if (!paths.length) {
                return
            }
            this.updateSelectedComponent(component => {
                const items = this.normalizeImageBannerProps(component)
                if (target.action === 'replace' && target.index >= 0 && items[target.index]) {
                    items[target.index].imageUrl = paths[0]
                    component.props.imageUrl = items[0] && items[0].imageUrl ? items[0].imageUrl : ''
                    return
                }
                const currentPaths = items.map(item => item.imageUrl)
                paths.forEach(path => {
                    if (currentPaths.indexOf(path) === -1) {
                        items.push({ imageUrl: path, linkType: 'none' })
                        currentPaths.push(path)
                    }
                })
                component.props.imageUrl = items[0] && items[0].imageUrl ? items[0].imageUrl : ''
            })
        },
        updateImageBannerItem(index, key, value) {
            this.updateSelectedComponent(component => {
                const items = this.normalizeImageBannerProps(component)
                const item = items[index]
                if (!item) {
                    return
                }
                item[key] = value
                if (key === 'linkType' && value === 'none') {
                    item.linkTarget = ''
                    item.linkName = ''
                }
                component.props.imageUrl = items[0] && items[0].imageUrl ? items[0].imageUrl : ''
            })
        },
        updateImageBannerLinkTarget(index, linkType, value) {
            const options = linkType === 'systemPage' ? this.systemPageOptions : this.customPageOptions
            const target = options.find(item => item.value === value)
            this.updateSelectedComponent(component => {
                const items = this.normalizeImageBannerProps(component)
                const item = items[index]
                if (!item) {
                    return
                }
                item.linkType = linkType
                item.linkTarget = value
                item.linkName = target ? target.label : value
            })
        },
        removeImageBannerItem(index) {
            this.updateSelectedComponent(component => {
                const items = this.normalizeImageBannerProps(component)
                items.splice(index, 1)
                component.props.imageUrl = items[0] && items[0].imageUrl ? items[0].imageUrl : ''
            })
        },
        moveImageBannerItem(index, delta) {
            this.updateSelectedComponent(component => {
                const items = this.normalizeImageBannerProps(component)
                const target = index + delta
                if (target < 0 || target >= items.length) {
                    return
                }
                const current = items[index]
                items.splice(index, 1)
                items.splice(target, 0, current)
                component.props.imageUrl = items[0] && items[0].imageUrl ? items[0].imageUrl : ''
            })
        },
        addNavItem() {
            this.updateSelectedComponent(component => {
                if (!component.props) {
                    component.props = {}
                }
                const items = Array.isArray(component.props.items) ? component.props.items : []
                items.push({
                    label: '新入口',
                    iconType: 'preset',
                    icon: 'new',
                    linkType: 'none'
                })
                component.props.items = items
            })
        },
        updateNavItem(index, key, value) {
            this.updateSelectedComponent(component => {
                const items = component.props && Array.isArray(component.props.items) ? component.props.items : []
                const item = items[index]
                if (!item) {
                    return
                }
                item[key] = value
                if (key === 'iconType' && value === 'preset' && !item.icon) {
                    item.icon = 'new'
                }
                if (key === 'linkType' && value === 'none') {
                    item.linkTarget = ''
                    item.linkName = ''
                }
            })
        },
        updateNavLinkTarget(index, linkType, value) {
            const options = linkType === 'systemPage' ? this.systemPageOptions : this.customPageOptions
            const target = options.find(item => item.value === value)
            this.updateSelectedComponent(component => {
                const items = component.props && Array.isArray(component.props.items) ? component.props.items : []
                const item = items[index]
                if (!item) {
                    return
                }
                item.linkType = linkType
                item.linkTarget = value
                item.linkName = target ? target.label : value
            })
        },
        openNavIconImagePicker(index) {
            const nav = this.navItems[index]
            this.imagePickerTarget = {
                scope: 'navIcon',
                index
            }
            this.imagePickerValue = nav && nav.iconImage ? nav.iconImage : ''
            this.imagePickerMultiple = false
            this.imagePickerVisible = true
        },
        removeNavItem(index) {
            this.updateSelectedComponent(component => {
                const items = component.props && Array.isArray(component.props.items) ? component.props.items : []
                items.splice(index, 1)
                component.props.items = items
            })
        },
        moveNavItem(index, delta) {
            this.updateSelectedComponent(component => {
                const items = component.props && Array.isArray(component.props.items) ? component.props.items : []
                const target = index + delta
                if (target < 0 || target >= items.length) {
                    return
                }
                const current = items[index]
                items.splice(index, 1)
                items.splice(target, 0, current)
                component.props.items = items
            })
        },
        openGoodsPicker() {
            const currentItems = this.selectedGoodsItems
            const needHydrate = currentItems.some(item => item.goodsId && !item.goodsName)
            if (!needHydrate) {
                this.goodsPickerValue = currentItems
                this.goodsPickerVisible = true
                return
            }
            const goodsIds = currentItems.map(item => item.goodsId).filter(Boolean)
            selectMallDecorateGoods({
                loadType: 2,
                storeShopId: this.storeShopId,
                goodsId: goodsIds.join(',')
            }).then(res => {
                const hydratedItems = res.success ? this.sortGoodsByIds(res.result || [], goodsIds) : currentItems
                this.goodsPickerValue = hydratedItems.length ? hydratedItems : currentItems
                this.applyGoodsSelection(this.goodsPickerValue)
                this.goodsPickerVisible = true
            }).catch(error => {
                this.goodsPickerValue = currentItems
                this.$message.warning(this.getErrorMessage(error, '已选商品回显失败，可重新选择商品'))
                this.goodsPickerVisible = true
            })
        },
        applyGoodsSelection(goodsList) {
            const snapshots = this.normalizeGoodsSnapshots(goodsList)
            this.updateSelectedComponent(component => {
                if (!component.dataSource) {
                    component.dataSource = {}
                }
                component.dataSource.mode = 'manual'
                component.dataSource.mock = false
                component.dataSource.goodsIds = snapshots.map(item => item.goodsId)
                component.dataSource.goodsSnapshots = snapshots
            })
        },
        normalizeGoodsSnapshots(goodsList) {
            return (Array.isArray(goodsList) ? goodsList : []).filter(item => item && item.goodsId).map(item => ({
                goodsId: item.goodsId,
                goodsNo: item.goodsNo,
                goodsName: item.goodsName,
                image: item.image || item.imageUrl,
                unitPrice: item.unitPrice,
                retailPrice: item.retailPrice
            }))
        },
        sortGoodsByIds(goodsList, goodsIds) {
            const goodsMap = {}
            const sourceGoodsList = goodsList || []
            sourceGoodsList.forEach(item => {
                goodsMap[item.goodsId] = item
            })
            return goodsIds.map(goodsId => goodsMap[goodsId] || { goodsId })
        },
        removeGoodsItem(index) {
            const items = this.normalizeGoodsSnapshots(this.selectedGoodsItems)
            items.splice(index, 1)
            this.applyGoodsSelection(items)
        },
        moveGoodsItem(index, delta) {
            const items = this.normalizeGoodsSnapshots(this.selectedGoodsItems)
            const target = index + delta
            if (target < 0 || target >= items.length) {
                return
            }
            const current = items[index]
            items.splice(index, 1)
            items.splice(target, 0, current)
            this.applyGoodsSelection(items)
        },
        getGoodsPrice(goods) {
            if (!goods) {
                return '0'
            }
            const price = goods.unitPrice !== undefined && goods.unitPrice !== null ? goods.unitPrice : goods.retailPrice
            return price !== undefined && price !== null && price !== '' ? price : '0'
        },
        handleCouponDropdown(open) {
            if (open && !this.couponOptions.length) {
                this.loadCouponOptions()
            }
        },
        loadCouponOptions() {
            if (!this.storeShopId) {
                return
            }
            this.couponLoading = true
            selectMallDecorateCoupon({ storeShopId: this.storeShopId }).then(res => {
                if (res.success) {
                    this.couponOptions = res.result || []
                }
            }).catch(error => {
                this.couponOptions = []
                this.$message.warning(this.getErrorMessage(error, '优惠券加载失败'))
            }).finally(() => {
                this.couponLoading = false
            })
        },
        getCouponOptionLabel(item) {
            if (!item) {
                return ''
            }
            return `${item.couponName || '优惠券'} · ${item.couponValue || ''} · ${item.couponLabel || ''}`
        },
        getCouponLabel(coupon) {
            if (!coupon) {
                return ''
            }
            if (coupon.threshold) {
                return `满${coupon.threshold}可用`
            }
            return coupon.title || '无限制'
        },
        addCouponItem(couponId) {
            if (!couponId) {
                return
            }
            const coupon = this.couponOptions.find(item => item.couponId === couponId)
            if (!coupon) {
                return
            }
            this.updateSelectedComponent(component => {
                if (!component.props) {
                    component.props = {}
                }
                const items = Array.isArray(component.props.items) ? component.props.items : []
                if (items.find(item => item.couponId === couponId)) {
                    this.$message.warning('该优惠券已添加')
                    return
                }
                items.push({
                    couponId: coupon.couponId,
                    couponName: coupon.couponName,
                    couponValue: coupon.couponValue,
                    couponLabel: coupon.couponLabel
                })
                component.props.items = items
            })
        },
        removeCouponItem(index) {
            this.updateSelectedComponent(component => {
                const items = component.props && Array.isArray(component.props.items) ? component.props.items : []
                items.splice(index, 1)
                component.props.items = items
            })
        },
        moveCouponItem(index, delta) {
            this.updateSelectedComponent(component => {
                const items = component.props && Array.isArray(component.props.items) ? component.props.items : []
                const target = index + delta
                if (target < 0 || target >= items.length) {
                    return
                }
                const current = items[index]
                items.splice(index, 1)
                items.splice(target, 0, current)
                component.props.items = items
            })
        },
        updateOpenCardFeatures(text) {
            const items = (text || '').split('\n').map(item => item.trim()).filter(Boolean).map(item => {
                const parts = item.split('|')
                return {
                    title: parts[0] || item,
                    subtitle: parts[1] || '',
                    icon: parts[2] || 'gift'
                }
            })
            this.updatePageConfig('featureList', items)
        },
        updateUserCenterFeatures(text) {
            const items = (text || '').split('\n').map(item => item.trim()).filter(Boolean).map(item => {
                const parts = item.split('|')
                return {
                    title: parts[0] || item,
                    subtitle: parts[1] || '',
                    icon: parts[2] || 'service'
                }
            })
            this.updatePageConfig('featureList', items)
        },
        getPageConfig(key, defaultValue = '') {
            const content = this.parsedContent
            return content && content.config && content.config[key] !== undefined ? content.config[key] : defaultValue
        },
        updatePageConfig(key, value) {
            this.updateDraft(model => {
                if (!model.config) {
                    model.config = {}
                }
                model.config[key] = value
            })
        },
        updateHomeTitleMode(value) {
            this.updateDraft(model => {
                if (!model.config) {
                    model.config = {}
                }
                model.config.titleMode = value
                if (value === 'shopName') {
                    model.config.pageTitle = model.config.storeShopName || '商城首页'
                }
            })
        },
        saveDraft() {
            if (!this.currentPage) {
                return
            }
            try {
                JSON.parse(this.draftContent)
            } catch (e) {
                this.$message.error('JSON 格式不正确，不能保存')
                return
            }
            this.loading = true
            saveMallPageV2Draft({
                storeShopId: this.storeShopId,
                pageType: this.currentPage.pageType,
                draftContent: this.draftContent
            })
                .then(res => {
                    if (res.success) {
                        this.$message.success('已保存草稿')
                        this.loadAll()
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        },
        saveCurrentDraftForPublish() {
            if (!this.currentPage) {
                return Promise.resolve()
            }
            try {
                JSON.parse(this.draftContent)
            } catch (e) {
                this.$message.error('JSON 格式不正确，不能发布')
                return Promise.reject(e)
            }
            return saveMallPageV2Draft({
                storeShopId: this.storeShopId,
                pageType: this.currentPage.pageType,
                draftContent: this.draftContent
            }).then(res => {
                if (!res.success) {
                    return Promise.reject(new Error(res.message || '保存当前草稿失败'))
                }
                return res
            })
        },
        getErrorMessage(error, fallback) {
            if (error && error.response && error.response.data && error.response.data.message) {
                return error.response.data.message
            }
            if (error && error.message) {
                return error.message
            }
            return fallback
        },
        openCustomPageModal(mode, page) {
            this.customPageModalMode = mode
            this.customPageForm = {
                pageType: page && page.pageType ? page.pageType : '',
                pageName: page && page.pageName ? page.pageName : ''
            }
            this.customPageModalVisible = true
        },
        submitCustomPage() {
            const pageName = (this.customPageForm.pageName || '').trim()
            if (!pageName) {
                this.$message.warning('请输入页面名称')
                return
            }
            this.loading = true
            const action = this.customPageModalMode === 'rename' ? renameMallCustomPageV2 : addMallCustomPageV2
            action({
                storeShopId: this.storeShopId,
                pageType: this.customPageForm.pageType,
                pageName
            }).then(res => {
                if (res.success) {
                    this.$message.success(this.customPageModalMode === 'rename' ? '页面名称已更新' : '自定义页面已创建')
                    this.customPageModalVisible = false
                    if (this.customPageModalMode === 'add' && res.result && res.result.pageType) {
                        this.currentPage = null
                        this.loadAll().then(() => this.selectPage(res.result.pageType))
                    } else {
                        this.loadAll().then(() => this.selectPage(this.customPageForm.pageType))
                    }
                }
            }).finally(() => {
                this.loading = false
            })
        },
        deleteCustomPage(page) {
            this.loading = true
            deleteMallCustomPageV2({
                storeShopId: this.storeShopId,
                pageType: page.pageType
            }).then(res => {
                if (res.success) {
                    this.$message.success('自定义页面已删除')
                    if (this.currentPage && this.currentPage.pageType === page.pageType) {
                        this.currentPage = null
                    }
                    this.loadAll()
                }
            }).finally(() => {
                this.loading = false
            })
        },
        openCopyDecorateModal() {
            if (!this.storeShopId) {
                this.$message.warning('缺少当前商城ID')
                return
            }
            this.copyForm.sourceStoreShopId = ''
            this.copyModalVisible = true
            this.loadCopySourceStores()
        },
        handleCopySourceDropdown(open) {
            if (open && !this.copySourceList.length) {
                this.loadCopySourceStores()
            }
        },
        loadCopySourceStores(keyword = '') {
            if (!this.storeShopId) {
                return
            }
            this.copySourceLoading = true
            selectMallDecorateCopySourceStore({
                currentStoreShopId: this.storeShopId,
                keyword
            }).then(res => {
                if (res.success) {
                    this.copySourceList = res.result || []
                }
            }).catch(error => {
                this.copySourceList = []
                this.$message.warning(this.getErrorMessage(error, '来源商城加载失败'))
            }).finally(() => {
                this.copySourceLoading = false
            })
        },
        submitCopyDecorate() {
            if (!this.copyForm.sourceStoreShopId) {
                this.$message.warning('请选择来源商城')
                return
            }
            const source = this.copySourceList.find(item => item.value === this.copyForm.sourceStoreShopId)
            this.$confirm({
                title: '确认覆盖当前商城装修草稿？',
                content: `将从「${source ? source.label : '所选商城'}」复制整套装修到当前商城，当前商城草稿和自定义页会被覆盖，已发布版本会清空。`,
                onOk: () => {
                    this.copying = true
                    return copyMallDecorateV2FromStore({
                        sourceStoreShopId: this.copyForm.sourceStoreShopId,
                        targetStoreShopId: this.storeShopId,
                        overwrite: true
                    }).then(res => {
                        if (res.success) {
                            this.$message.success('装修草稿复制成功，请预览确认后发布')
                            this.copyModalVisible = false
                            this.currentPage = null
                            return this.loadAll()
                        }
                    }).catch(error => {
                        this.$message.error(this.getErrorMessage(error, '复制失败'))
                        return Promise.reject(error)
                    }).finally(() => {
                        this.copying = false
                    })
                }
            })
        },
        publishAll() {
            this.$confirm({
                title: '确认整套发布？',
                content: '会先保存当前正在编辑的页面，再同时发布固定页面和自定义页面。',
                onOk: () => {
                    this.loading = true
                    return this.saveCurrentDraftForPublish()
                        .then(() => publishMallPageV2(this.storeShopId))
                        .then(res => {
                            if (res.success) {
                                this.$message.success(res.message || '发布成功，小程序端刷新后即可查看')
                                this.loadAll()
                            } else {
                                this.$message.error(res.message || '发布失败')
                            }
                        })
                        .catch(error => {
                            this.$message.error(this.getErrorMessage(error, '发布失败，请检查首页商品组件和页面配置'))
                            return Promise.reject(error)
                        })
                        .finally(() => {
                            this.loading = false
                        })
                }
            })
        },
        applyTemplate(template) {
            this.$confirm({
                title: `应用模板：${template.templateName}`,
                content: '模板会覆盖四类页面草稿，但不会影响已发布版本。',
                onOk: () => {
                    this.loading = true
                    return applyMallTemplateV2({
                        storeShopId: this.storeShopId,
                        templateId: template.id,
                        templateCode: template.templateCode
                    })
                        .then(res => {
                            if (res.success) {
                                this.$message.success('模板已应用')
                                this.currentPage = null
                                this.loadAll()
                            }
                        })
                        .finally(() => {
                            this.loading = false
                        })
                }
            })
        },
        toggleV2(checked) {
            this.loading = true
            enableMallDecorateV2({
                storeShopId: this.storeShopId,
                useDecorateV2: checked
            })
                .then(res => {
                    if (res.success) {
                        this.useDecorateV2 = checked
                        this.$message.success('开关已更新')
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        }
    },
    created() {
        this.storeShopId = this.$route.query.storeShopId || this.$route.query.id
        this.useDecorateV2 = this.$route.query.useDecorateV2 === 'true'
        this.loadSelectors()
        this.loadAll()
    }
}
</script>

<style scoped>
.mall-v2-page {
    padding: 12px;
}

.mall-v2-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding: 12px 16px;
    background: #ffffff;
}

.mall-v2-title {
    font-size: 18px;
    font-weight: 600;
    color: #222222;
}

.mall-v2-subtitle {
    margin-top: 4px;
    color: #888888;
}

.mall-v2-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.mall-v2-layout {
    display: grid;
    grid-template-columns: 360px 390px minmax(320px, 1fr);
    gap: 12px;
}

.mall-v2-sidebar,
.mall-v2-preview,
.mall-v2-editor {
    min-height: 640px;
    padding: 12px;
    background: #ffffff;
}

.mall-v2-sidebar {
    padding: 16px;
}

.mall-v2-sidebar /deep/ .ant-tabs-bar {
    margin-bottom: 14px;
}

.mall-v2-sidebar /deep/ .ant-tabs-tab {
    padding: 8px 16px;
}

.mall-v2-sidebar /deep/ .ant-list-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    padding: 10px 12px;
    border-bottom: 0;
    border-radius: 4px;
    cursor: pointer;
}

.mall-v2-sidebar /deep/ .ant-list-item:hover {
    background: #f7f9fc;
}

.custom-page-add {
    width: 100%;
    margin-bottom: 10px;
}

.page-list-item-main {
    min-width: 0;
}

.page-list-actions {
    display: flex;
    flex-shrink: 0;
    gap: 6px;
    margin-left: 8px;
}

.mall-v2-list-title {
    color: #222222;
    font-weight: 500;
}

.mall-v2-list-desc,
.editor-subtitle {
    color: #999999;
    font-size: 12px;
}

.active {
    background: #f0f5ff;
}

.template-row {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.theme-section {
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f0f0;
}

.sidebar-config-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
    color: #333333;
    font-size: 13px;
}

.sidebar-config-field {
    margin-top: 10px;
}

.sidebar-config-field label {
    display: block;
    margin-bottom: 6px;
    color: #666666;
    font-size: 12px;
}

.home-title-mode-group {
    display: flex;
    width: 100%;
}

.home-title-mode-group /deep/ .ant-radio-button-wrapper {
    flex: 1;
    min-width: 0;
    padding: 0 8px;
    text-align: center;
    white-space: nowrap;
}

.sidebar-section-title {
    margin-bottom: 8px;
    color: #222222;
    font-weight: 600;
}

.sidebar-section-desc {
    margin-top: -4px;
    margin-bottom: 10px;
    color: #999999;
    font-size: 12px;
}

.theme-card {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr) 16px;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    padding: 10px 12px;
    border: 1px solid #edf0f5;
    border-radius: 6px;
    background: #ffffff;
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}

.theme-card:hover,
.theme-card.active {
    border-color: #91caff;
    background: #f7fbff;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.1);
}

.theme-card-main {
    min-width: 0;
}

.theme-swatch {
    display: inline-block;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.08);
}

.sidebar-control-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.sidebar-control-card {
    min-height: 116px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}

.sidebar-control-card:hover {
    border-color: #91caff;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.12);
    transform: translateY(-1px);
}

.sidebar-control-card .control-card-desc {
    flex: 1;
    margin-bottom: 8px;
    line-height: 1.45;
}

.sidebar-control-card .ant-btn {
    align-self: flex-start;
}

.phone-frame {
    width: 360px;
    height: 684px;
    margin: 0 auto;
    border: 1px solid #eeeeee;
    background: #f7f7f7;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.phone-title {
    height: 44px;
    flex-shrink: 0;
    line-height: 44px;
    text-align: center;
    background: #ffffff;
    font-weight: 600;
}

.editor-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.editor-gap {
    margin-top: 16px;
}

.editor-title {
    font-weight: 600;
    color: #222222;
}

.component-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.component-card,
.control-card {
    padding: 10px 12px;
    border: 1px solid #eeeeee;
    border-radius: 8px;
    background: #fafafa;
}

.component-card {
    cursor: pointer;
}

.component-card.active {
    border-color: #1890ff;
    background: #eef6ff;
}

.component-card-main {
    margin-bottom: 8px;
}

.component-card-title,
.control-card-title {
    color: #222222;
    font-weight: 600;
}

.component-card-desc,
.control-card-desc {
    margin-top: 4px;
    color: #888888;
    font-size: 12px;
    line-height: 1.5;
}

.component-card-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.config-panel {
    padding: 12px;
    border: 1px solid #e8edf3;
    border-radius: 8px;
    background: #fbfcfe;
}

.config-section-title {
    margin: 14px 0 10px;
    padding-left: 8px;
    border-left: 3px solid #1890ff;
    color: #222222;
    font-weight: 600;
}

.config-section-title:first-child {
    margin-top: 0;
}

.config-row {
    display: grid;
    grid-template-columns: 86px minmax(0, 1fr);
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.config-row-top {
    align-items: flex-start;
}

.config-row-top label {
    padding-top: 7px;
}

.config-row:last-child {
    margin-bottom: 0;
}

.config-row label {
    color: #555555;
    font-size: 12px;
    text-align: right;
}

.config-row /deep/ .ant-switch {
    width: auto;
    min-width: 44px;
    justify-self: flex-start;
}

.swiper-editor-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.swiper-editor-item {
    display: flex;
    gap: 10px;
    padding: 10px;
    border: 1px solid #e8edf3;
    border-radius: 6px;
    background: #ffffff;
}

.swiper-editor-preview {
    width: 92px;
    height: 68px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 4px;
    background: #f5f7fa;
    color: #bfbfbf;
    font-size: 12px;
}

.swiper-editor-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.swiper-editor-main {
    min-width: 0;
    flex: 1;
}

.swiper-editor-head,
.swiper-editor-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.swiper-editor-head {
    justify-content: space-between;
    margin-bottom: 8px;
}

.swiper-editor-head span {
    color: #222222;
    font-weight: 600;
}

.swiper-editor-head .ant-btn {
    margin-left: 4px;
}

.swiper-editor-row {
    margin-top: 8px;
}

.banner-editor {
    min-width: 0;
    width: 100%;
}

.banner-editor-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 10px;
}

.banner-editor-item {
    display: flex;
    gap: 10px;
    min-width: 0;
    padding: 10px;
    border: 1px solid #e8edf3;
    border-radius: 6px;
    background: #ffffff;
}

.banner-editor-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 112px;
    height: 70px;
    overflow: hidden;
    border-radius: 4px;
    background: #f5f7fa;
    color: #bfbfbf;
    font-size: 12px;
}

.banner-editor-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.banner-editor-main {
    min-width: 0;
    flex: 1;
}

.banner-editor-head,
.banner-editor-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.banner-editor-head {
    justify-content: space-between;
    margin-bottom: 8px;
}

.banner-editor-head span {
    color: #222222;
    font-weight: 600;
}

.banner-editor-head .ant-btn {
    margin-left: 4px;
}

.banner-editor-row {
    margin-top: 8px;
}

.nav-editor-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.nav-editor-item {
    padding: 10px;
    border: 1px solid #e8edf3;
    border-radius: 6px;
    background: #ffffff;
}

.coupon-editor-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.coupon-editor-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px;
    border: 1px solid #e8edf3;
    border-radius: 6px;
    background: #ffffff;
}

.coupon-editor-main {
    min-width: 0;
    display: flex;
    flex: 1;
    flex-direction: column;
}

.coupon-editor-main span {
    overflow: hidden;
    color: #222222;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.coupon-editor-main em {
    margin-top: 4px;
    overflow: hidden;
    color: #8c8c8c;
    font-size: 12px;
    font-style: normal;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.coupon-editor-actions {
    display: flex;
    flex-shrink: 0;
    gap: 4px;
}

.selected-goods-editor {
    min-width: 0;
    width: 100%;
}

.selected-goods-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 10px;
    max-height: 360px;
    overflow-y: auto;
}

.selected-goods-card {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 78px;
    min-width: 0;
    padding: 10px;
    border: 1px solid #e8edf3;
    border-radius: 6px;
    background: #ffffff;
}

.selected-goods-image {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 56px;
    height: 56px;
    border-radius: 6px;
    background: #f5f6f8;
    overflow: hidden;
}

.selected-goods-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.selected-goods-info {
    min-width: 0;
    flex: 1;
    padding-right: 4px;
}

.selected-goods-name,
.selected-goods-no {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.selected-goods-name {
    color: #222222;
    font-weight: 600;
}

.selected-goods-no {
    margin-top: 4px;
    color: #8c8c8c;
    font-size: 12px;
}

.selected-goods-price {
    margin-top: 4px;
    color: #d64545;
    font-weight: 700;
}

.selected-goods-actions {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 4px;
}

.selected-goods-actions /deep/ .ant-btn {
    width: 28px;
    height: 26px;
    padding: 0;
}

.nav-editor-head,
.nav-editor-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.nav-editor-head {
    justify-content: space-between;
    margin-bottom: 8px;
}

.nav-editor-head span {
    color: #222222;
    font-weight: 600;
}

.nav-editor-row {
    margin-top: 8px;
}

.control-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.editor-actions {
    margin-top: 12px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.copy-modal-tip {
    margin-bottom: 16px;
    padding: 10px 12px;
    border-radius: 6px;
    background: #fff7e6;
    color: #8a5a00;
    line-height: 1.6;
}

.copy-modal-field label {
    display: block;
    margin-bottom: 8px;
    color: #222222;
    font-weight: 600;
}
</style>
